// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useIsGuest } from "../../contexts/GuestModeContext"; // [2026-07-06] GUEST_ACCESS: lock paging for guests
import styles from "./PaginationControls.module.scss";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  loading?: boolean;
}

export function PaginationControls({
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  loading = false,
}: PaginationControlsProps) {
  const [jumpToPage, setJumpToPage] = useState("");
  // [2026-07-06] GUEST_ACCESS: guests can't paginate (anti-scrape + sign-up nudge) — all
  // paging controls are disabled; they see only the first page of masked results.
  const isGuest = useIsGuest();

  // Calculate range of items being displayed
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleJumpToPage = () => {
    const pageNum = parseInt(jumpToPage);
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
      setJumpToPage("");
    }
  };

  const handleJumpToPageKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleJumpToPage();
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = parseInt(e.target.value);
    onItemsPerPageChange(newItemsPerPage);
  };

  if (totalPages === 0) {
    return null;
  }

  // [2026-02-07] style-extraction: moved inline styles to SCSS module
  return (
    <div
      className={`d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 py-3 border-top ${styles.container} pagination-mobile`}
    >
      {/* Left: Items per page selector */}
      <div className={`d-flex align-items-center gap-2 ${styles.hideOnMobile}`}>
        <label
          htmlFor="items-per-page"
          className={`p-small text-muted mb-0 ${styles.label}`}
        >
          Items per page:
        </label>
        <Form.Select
          id="items-per-page"
          size="sm"
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
          disabled={loading || isGuest}
          title={isGuest ? "Sign in to change page size" : undefined}
          className={styles.select}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </Form.Select>
      </div>

      {/* Center: Page navigation */}
      <div className="d-flex align-items-center gap-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handlePrevious}
          disabled={currentPage === 1 || loading || isGuest}
          title={isGuest ? "Sign in to browse more pages" : undefined}
          className={styles.button}
        >
          Previous
        </Button>
        <span className={`p-small text-muted ${styles.pageIndicator}`}>
          {currentPage} out of {totalPages} {totalPages === 1 ? "page" : "pages"}
        </span>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleNext}
          disabled={currentPage === totalPages || loading || isGuest}
          title={isGuest ? "Sign in to browse more pages" : undefined}
          className={styles.button}
        >
          Next
        </Button>
      </div>

      {/* Right: Jump to page and results count */}
      <div className="d-flex align-items-center gap-2">
        <span className={`p-small text-muted ${styles.resultsCount}`}>
          Showing {startItem}-{endItem} of {totalItems}
        </span>
        <div className={`d-flex align-items-center gap-1 ${styles.hideOnMobile}`}>
          <Form.Control
            type="number"
            size="sm"
            placeholder="Page"
            value={jumpToPage}
            onChange={(e) => setJumpToPage(e.target.value)}
            onKeyPress={handleJumpToPageKeyPress}
            disabled={loading || isGuest}
            title={isGuest ? "Sign in to jump to a page" : undefined}
            min={1}
            max={totalPages}
            className={styles.jumpInput}
          />
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleJumpToPage}
            disabled={loading || !jumpToPage || isGuest}
            title={isGuest ? "Sign in to jump to a page" : undefined}
            className={styles.button}
          >
            Go
          </Button>
        </div>
      </div>
    </div>
  );
}
